// Apolo IDE — CommandCenterModal (sprint command-center).
//
// A visão MACRO sobre TODAS as sessões de código cruzando projetos/pastas.
// Resolve o "me perco com várias sessões em paralelo": um modal full-bleed
// com um board kanban READ-ONLY. A sidebar continua o roteador compacto; isto
// é o painel de controle.
//
// REGRAS (não viole):
//  1. Status é DERIVADO do event log — o board só REFLETE. SEM drag-pra-mudar.
//     Card é clique-pra-abrir a sessão (setActiveSession + fecha).
//  2. Atenção primeiro: a faixa "Precisa de você agora" responde antes do resto.
//  3. Sem glow: vibração vem de camadas (surface + border + texto), não de
//     shadow colorido. Sombra neutra sutil pra elevação é ok.
//  4. Guards contra undefined em todo lugar (sessões/status/folders ausentes) —
//     um throw no render = tela preta (há ErrorBoundary no root).
//
// Visual espelha _inspiration/code-sidebar/command-center-mockup.html, traduzido
// pros tokens --apolo-* (sem hex hardcoded) e reusando os componentes/regras
// existentes: SessionStatusBadge, sessionStatusMeta, groupSessionsByStatus,
// BOARD_ORDER, ATTENTION_STATUSES. A animação de entrada é self-contained
// (style scoped por data-attr) e respeita prefers-reduced-motion.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, LayoutGrid, Search, X, AlertCircle } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { sessionStatusMeta, ATTENTION_STATUSES } from '../../lib/code/sessionStatusMeta.js';
import { groupSessionsByStatus, BOARD_ORDER } from '../../lib/code/sessionGrouping.js';
import SessionStatusBadge from './SessionStatusBadge.jsx';

// Cor (token --apolo-*) por status derivado. Reforço visual da pill/borda —
// nunca o ÚNICO sinal (a pill carrega ícone+texto). Mantém o mesmo mapa
// semântico do sessionStatusMeta (danger/accent/warn/info/neutral).
const STATUS_VAR = {
  blocked: 'var(--apolo-error)',
  review: 'var(--apolo-accent)',
  needs_you: 'var(--apolo-warning)',
  running: 'var(--apolo-info)',
  idle: 'var(--apolo-textTertiary)',
};

// Cor (token) por chave de cor de pasta. folder.color ∈ accent|success|warning|info
// (schema 0007_code_folders: DEFAULT 'accent'). Fallback → accent.
const FOLDER_VAR = {
  accent: 'var(--apolo-accent)',
  success: 'var(--apolo-success)',
  warning: 'var(--apolo-warning)',
  info: 'var(--apolo-info)',
};
function folderColorVar(color) {
  return FOLDER_VAR[color] || FOLDER_VAR.accent;
}

// Projeto derivado do DIRETÓRIO real da sessão (o repo onde o agente roda) — é
// o que faz "Por projeto" virar um espelho dos projetos de verdade, sem pasta
// manual. Single-user: mapeamos os repos conhecidos do Pedro (brain + disco)
// pra nome/cor amigável; repo desconhecido cai no basename do diretório.
const PROJECT_HINTS = [
  { test: /(^|[/\\])apolo([/\\]|$)/i, key: 'apolo', label: 'Apolo', colorVar: 'var(--apolo-accent)' },
  { test: /glyco/i, key: 'glyco', label: 'Glyco', colorVar: 'var(--apolo-success)' },
  { test: /higgsfield/i, key: 'higgsfield', label: 'higgsfield', colorVar: 'var(--apolo-info)' },
  { test: /(liquid-glass|ios-26)/i, key: 'liquid-glass', label: 'Liquid Glass DS', colorVar: 'var(--apolo-warning)' },
  { test: /remotion/i, key: 'remotion', label: 'remotion', colorVar: 'var(--apolo-info)' },
  { test: /(medex|medash|medico-exponencial)/i, key: 'medex', label: 'Medex', colorVar: 'var(--apolo-warning)' },
];
function projectFromDirectory(dir) {
  if (!dir || typeof dir !== 'string') return null;
  for (const h of PROJECT_HINTS) if (h.test.test(dir)) return { key: h.key, label: h.label, colorVar: h.colorVar };
  const base = dir.replace(/[/\\]+$/, '').split(/[/\\]/).pop();
  if (!base) return null;
  return { key: `dir:${base.toLowerCase()}`, label: base, colorVar: 'var(--apolo-accent)' };
}
// Colunas fixas (sempre visíveis, mesmo vazias) — escolha do Pedro: Apolo + Glyco.
const PINNED_PROJECTS = [
  { key: 'apolo', label: 'Apolo', colorVar: 'var(--apolo-accent)' },
  { key: 'glyco', label: 'Glyco', colorVar: 'var(--apolo-success)' },
];
const SEM_PROJETO_KEY = '__sem_projeto__';

// CSS self-contained: animação de entrada (fade + scale .985→1 + translateY,
// ~240ms cubic-bezier(.16,1,.3,1)) e scrollbar discreta. Escopado por
// [data-apolo-cc] pra não vazar. prefers-reduced-motion → sem animação.
const CC_STYLE = `
[data-apolo-cc-backdrop]{animation:apoloCcFade .18s ease both}
[data-apolo-cc-dialog]{animation:apoloCcRise .24s cubic-bezier(.16,1,.3,1) both}
@keyframes apoloCcFade{from{opacity:0}to{opacity:1}}
@keyframes apoloCcRise{from{opacity:0;transform:scale(.985) translateY(10px)}to{opacity:1;transform:none}}
[data-apolo-cc-scroll]{scrollbar-width:thin;scrollbar-color:var(--apolo-borderStrong) transparent}
[data-apolo-cc-scroll]::-webkit-scrollbar{width:9px;height:9px}
[data-apolo-cc-scroll]::-webkit-scrollbar-thumb{background:var(--apolo-borderStrong);border-radius:8px;border:2px solid transparent;background-clip:padding-box}
[data-apolo-cc-scroll]::-webkit-scrollbar-track{background:transparent}
@media (prefers-reduced-motion:reduce){
  [data-apolo-cc-backdrop],[data-apolo-cc-dialog]{animation:none}
}
`;

// Tempo relativo curto e PT. Tolerante a undefined/strings inválidas → ''.
function relativeTime(value) {
  if (!value) return '';
  const then = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'agora';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'agora';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} sem`;
  const mo = Math.floor(day / 30);
  return `${mo} mês${mo > 1 ? 'es' : ''}`;
}

function statusOf(statuses, id) {
  return (statuses && statuses[id] && statuses[id].status) || 'idle';
}

export default function CommandCenterModal({ open, onClose }) {
  const sessions = useAgentStore((s) => s.code.sessions);
  const folders = useAgentStore((s) => s.code.folders);
  const sessionStatuses = useAgentStore((s) => s.code.sessionStatuses);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);

  const [view, setView] = useState('status'); // 'status' | 'project'
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // chip de atenção ativo
  const [query, setQuery] = useState('');

  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Listas defensivas — store pode estar vazio/loading. Memoizadas pra terem
  // identidade estável (evita refazer os memos de board a cada render e silencia
  // o react-hooks/exhaustive-deps).
  const allSessions = useMemo(() => (Array.isArray(sessions) ? sessions : []), [sessions]);
  const allFolders = useMemo(() => (Array.isArray(folders) ? folders : []), [folders]);
  const statuses = useMemo(() => sessionStatuses || {}, [sessionStatuses]);

  // Reset de estado efêmero ao reabrir (não persistimos filtros do painel).
  useEffect(() => {
    if (open) {
      setStatusFilter(null);
      setAttentionOnly(false);
      setQuery('');
    }
  }, [open]);

  // Foco inicial no modal; restaura o foco anterior ao fechar.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement;
      // foca o container do dialog (não o input — Esc deve fechar o modal,
      // e roubar foco pro search atrapalharia leitores de tela no abrir).
      const id = window.requestAnimationFrame(() => dialogRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    const prev = restoreFocusRef.current;
    if (prev && typeof prev.focus === 'function') prev.focus();
    return undefined;
  }, [open]);

  // Atalhos DENTRO do modal: Esc fecha (ou limpa o chip antes), S alterna a
  // visão, A alterna "só atenção". Ignora quando o usuário digita no campo.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (statusFilter) setStatusFilter(null);
        else onClose?.();
        return;
      }
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 's') {
        e.preventDefault();
        setView((v) => (v === 'status' ? 'project' : 'status'));
      } else if (k === 'a') {
        e.preventDefault();
        setAttentionOnly((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, statusFilter, onClose]);

  // Sessões após busca por título (funcional). Mantida fora dos memos de board
  // pra reuso entre as duas visões.
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSessions;
    return allSessions.filter((s) => (s?.name || '').toLowerCase().includes(q));
  }, [allSessions, query]);

  // Lista efetiva do board: busca → "só atenção" → chip de status.
  const effective = useMemo(() => {
    let list = searched;
    if (attentionOnly) {
      list = list.filter((s) => ATTENTION_STATUSES.includes(statusOf(statuses, s.id)));
    }
    if (statusFilter) {
      list = list.filter((s) => statusOf(statuses, s.id) === statusFilter);
    }
    return list;
  }, [searched, attentionOnly, statusFilter, statuses]);

  // Contagem de atenção (sobre TODAS as sessões, não sobre o filtro — é o
  // "quanto tem pra você agora" global). Reusa a MESMA regra do AttentionStrip.
  const attentionCounts = useMemo(() => {
    const counts = { blocked: 0, review: 0, needs_you: 0 };
    for (const s of allSessions) {
      const st = statusOf(statuses, s.id);
      if (st in counts) counts[st] += 1;
    }
    return counts;
  }, [allSessions, statuses]);

  const attentionTotal = attentionCounts.blocked + attentionCounts.review + attentionCounts.needs_you;

  // Projetos representados (pra subtítulo "M projetos"): pastas com sessão +
  // grupo Recents se houver sessão sem pasta.
  const projectCount = useMemo(() => {
    const folderIds = new Set();
    let hasRecents = false;
    for (const s of allSessions) {
      if (s?.folder_id) folderIds.add(s.folder_id);
      else hasRecents = true;
    }
    return folderIds.size + (hasRecents ? 1 : 0);
  }, [allSessions]);

  // Colunas — visão "Por status" (default).
  const statusColumns = useMemo(() => {
    if (view !== 'status') return [];
    const grouped = groupSessionsByStatus(effective, statuses);
    const byStatus = new Map(grouped.map((g) => [g.status, g.sessions]));
    // Quando há chip ativo, só essa coluna; quando "só atenção", só as de atenção.
    let order = BOARD_ORDER;
    if (statusFilter) order = [statusFilter];
    else if (attentionOnly) order = BOARD_ORDER.filter((k) => ATTENTION_STATUSES.includes(k));
    return order.map((status) => ({
      key: status,
      label: sessionStatusMeta(status).label,
      colorVar: STATUS_VAR[status] || STATUS_VAR.idle,
      sessions: byStatus.get(status) || [],
      // divisor sutil antes de "running" separa o grupo de atenção do resto.
      separator: status === 'running' && !statusFilter && !attentionOnly,
    }));
  }, [view, effective, statuses, statusFilter, attentionOnly]);

  // Colunas — visão "Por projeto": agrupa pelo DIRETÓRIO real de cada sessão
  // (o repo onde o agente roda), não por pasta manual. Apolo + Glyco ficam
  // sempre visíveis (pinned); os demais repos detectados entram em ordem
  // alfabética; "Sem projeto" (sessão sem diretório) por último.
  const projectColumns = useMemo(() => {
    if (view !== 'project') return [];
    const byKey = new Map();
    const ensure = (key, label, colorVar) => {
      if (!byKey.has(key)) byKey.set(key, { key, label, colorVar, sessions: [] });
      return byKey.get(key);
    };
    for (const p of PINNED_PROJECTS) ensure(p.key, p.label, p.colorVar); // existem mesmo vazias
    for (const s of effective) {
      const proj = projectFromDirectory(s?.directory);
      const col = proj
        ? ensure(proj.key, proj.label, proj.colorVar)
        : ensure(SEM_PROJETO_KEY, 'Sem projeto', 'var(--apolo-textTertiary)');
      col.sessions.push(s);
    }
    const pinnedKeys = new Set(PINNED_PROJECTS.map((p) => p.key));
    const pinnedCols = PINNED_PROJECTS.map((p) => byKey.get(p.key));
    const restCols = [...byKey.values()]
      .filter((c) => !pinnedKeys.has(c.key) && c.key !== SEM_PROJETO_KEY)
      .sort((a, b) => a.label.localeCompare(b.label));
    const semProjeto = byKey.get(SEM_PROJETO_KEY);
    const cols = [...pinnedCols, ...restCols, ...(semProjeto ? [semProjeto] : [])];
    return cols.map((c) => {
      const att = c.sessions.filter((s) => ATTENTION_STATUSES.includes(statusOf(statuses, s.id))).length;
      return {
        ...c,
        summary: att > 0 ? `${att} precisa${att > 1 ? 'm' : ''} de você` : 'tudo sob controle',
      };
    });
  }, [view, effective, statuses]);

  const columns = view === 'status' ? statusColumns : projectColumns;

  function openSession(id) {
    setActiveSession(id);
    onClose?.();
  }

  function goToActivity() {
    window.dispatchEvent(new CustomEvent('apolo:openActivity'));
    onClose?.();
  }

  if (!open) return null;

  const subtitle = `${allSessions.length} ${allSessions.length === 1 ? 'sessão' : 'sessões'} · ${projectCount} ${projectCount === 1 ? 'projeto' : 'projetos'}`;

  return (
    <div
      data-apolo-cc-backdrop=""
      className="fixed inset-0 z-50 bg-apolo-overlay backdrop-blur-sm"
      onMouseDown={(e) => {
        // fecha só no clique direto no backdrop (não em cliques que começam dentro).
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <style>{CC_STYLE}</style>
      <div
        ref={dialogRef}
        data-apolo-cc-dialog=""
        role="dialog"
        aria-modal="true"
        aria-labelledby="cc-title"
        tabIndex={-1}
        className="absolute inset-6 flex flex-col overflow-hidden rounded-apolo-2xl border border-apolo-borderStrong bg-apolo-surface1 shadow-2xl outline-none"
      >
        {/* Header */}
        <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-apolo-borderSubtle px-5 py-3">
          <div className="flex items-center gap-2.5">
            <LayoutGrid size={16} className="text-apolo-accent" aria-hidden="true" />
            <div className="flex flex-col">
              <h2 id="cc-title" className="text-sm font-semibold leading-tight text-apolo-textPrimary">
                Command Center
              </h2>
              <span className="font-mono text-[11px] leading-tight text-apolo-textTertiary">{subtitle}</span>
            </div>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* Segmented toggle [Por status | Por projeto] */}
            <div
              role="group"
              aria-label="Visão do board"
              className="inline-flex rounded-apolo-md border border-apolo-border bg-apolo-surface1 p-0.5"
            >
              {[
                { v: 'status', label: 'Por status' },
                { v: 'project', label: 'Por projeto' },
              ].map((opt) => {
                const on = view === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setView(opt.v)}
                    aria-pressed={on}
                    className={
                      'inline-flex cursor-pointer items-center justify-center rounded-[6px] px-3 py-1.5 text-[11px] font-medium transition ' +
                      (on
                        ? 'bg-apolo-surface4 text-apolo-textPrimary shadow-[inset_0_0_0_1px_var(--apolo-borderStrong)]'
                        : 'text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary')
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Toggle "Só atenção" */}
            <button
              type="button"
              onClick={() => setAttentionOnly((v) => !v)}
              aria-pressed={attentionOnly}
              className={
                'inline-flex cursor-pointer items-center gap-2 rounded-apolo-md border px-2.5 py-1.5 text-[11px] font-medium transition ' +
                (attentionOnly
                  ? 'border-apolo-warning bg-apolo-warning/10 text-apolo-warning'
                  : 'border-apolo-border bg-apolo-surface1 text-apolo-textSecondary hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary')
              }
            >
              <span
                aria-hidden="true"
                className={'h-1.5 w-1.5 rounded-full ' + (attentionOnly ? 'bg-apolo-warning' : 'bg-apolo-textTertiary')}
              />
              Só atenção
            </button>

            {/* Busca por título */}
            <label className="inline-flex w-48 items-center gap-2 rounded-apolo-md border border-apolo-border bg-apolo-surface1 px-2.5 py-1.5 text-apolo-textTertiary focus-within:border-apolo-accent">
              <Search size={14} aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar sessão…"
                aria-label="Buscar sessão por título"
                className="w-full bg-transparent text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
              />
            </label>

            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar (Esc)"
              title="Fechar (Esc)"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-apolo-md border border-apolo-border bg-apolo-surface1 text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Faixa de atenção */}
        <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-apolo-borderSubtle bg-apolo-surface1 px-5 py-2.5">
          <span className="flex items-center gap-2 text-[11px] font-semibold text-apolo-textSecondary">
            <AlertCircle size={13} className="text-apolo-warning" aria-hidden="true" />
            Precisa de você agora
            <span className="font-mono text-apolo-textPrimary">{attentionTotal}</span>
          </span>
          <div className="ml-1 flex flex-wrap gap-1.5">
            {ATTENTION_STATUSES.map((key) => {
              const on = statusFilter === key;
              const colorVar = STATUS_VAR[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter((cur) => (cur === key ? null : key))}
                  aria-pressed={on}
                  className={
                    'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] transition ' +
                    (on
                      ? 'text-apolo-textPrimary'
                      : 'border-apolo-border bg-apolo-surface2 text-apolo-textSecondary hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary')
                  }
                  style={on ? { borderColor: colorVar, color: colorVar } : undefined}
                >
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-sm" style={{ background: colorVar }} />
                  {sessionStatusMeta(key).label}
                  <b className="font-mono">{attentionCounts[key] ?? 0}</b>
                </button>
              );
            })}
          </div>
        </div>

        {/* Agentes & workflows ficam no painel global de Atividade. */}
        <div className="flex shrink-0 items-center gap-2.5 border-b border-apolo-borderSubtle bg-apolo-surface1 px-5 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-apolo-accent">
            Agentes
          </span>
          <button
            type="button"
            onClick={goToActivity}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-apolo-md border border-apolo-border bg-apolo-surface1 px-2.5 py-1 text-[11px] font-medium text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
          >
            Ver na atividade
            <ArrowRight size={12} aria-hidden="true" />
          </button>
        </div>

        {/* Board */}
        <div data-apolo-cc-scroll="" className="flex-1 overflow-auto px-5 py-4">
          {allSessions.length === 0 || columns.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="font-mono text-xs text-apolo-textTertiary">
                {allSessions.length === 0 ? 'Nenhuma sessão ainda.' : 'Nada bate com este filtro.'}
              </p>
            </div>
          ) : (
            <div className="flex min-h-full items-start gap-3.5">
              {columns.map((col) => (
                <BoardColumn
                  key={col.key}
                  column={col}
                  view={view}
                  statuses={statuses}
                  folders={allFolders}
                  onOpenSession={openSession}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t border-apolo-borderSubtle bg-apolo-surface1 px-5 py-2.5 text-[11px] text-apolo-textTertiary">
          <span className="flex items-center gap-1.5">
            <Kbd>Tab</Kbd> navegar
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd> abrir sessão
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>S</Kbd> status/projeto
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>A</Kbd> só atenção
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>Esc</Kbd> fechar
          </span>
          <span className="ml-auto font-mono text-apolo-textTertiary">
            status derivado do event log — nunca manual
          </span>
        </footer>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="inline-block rounded-apolo-sm border border-apolo-border bg-apolo-surface3 px-1.5 font-mono text-[10px] leading-[17px] text-apolo-textSecondary">
      {children}
    </kbd>
  );
}

// Coluna do board. Header (marca de cor + label + contador [+ resumo no modo
// projeto]) e corpo com cards. Coluna vazia mostra estado discreto.
function BoardColumn({ column, view, statuses, folders, onOpenSession }) {
  const { label, colorVar, sessions, summary, separator } = column;
  const list = Array.isArray(sessions) ? sessions : [];
  return (
    <section
      className={
        'flex w-[290px] shrink-0 flex-col self-stretch rounded-apolo-xl border border-apolo-borderSubtle bg-apolo-surface1 ' +
        (separator ? 'relative ml-2.5' : '')
      }
    >
      {separator && (
        <span
          aria-hidden="true"
          className="absolute -left-3 bottom-1.5 top-1.5 w-px bg-gradient-to-b from-apolo-border to-transparent"
        />
      )}
      <header className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-2.5">
        <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: colorVar }} />
        <span className="text-xs font-semibold text-apolo-textPrimary">{label}</span>
        <span className="ml-auto rounded-apolo-md border border-apolo-border bg-apolo-surface3 px-1.5 py-px font-mono text-[11px] text-apolo-textSecondary">
          {list.length}
        </span>
      </header>
      {summary && (
        <p className="px-3 pb-0.5 pt-1.5 font-mono text-[10.5px] text-apolo-textTertiary">{summary}</p>
      )}
      <div data-apolo-cc-scroll="" className="flex flex-col gap-2.5 overflow-auto p-2.5">
        {list.length === 0 ? (
          <p className="py-3 text-center font-mono text-[11px] text-apolo-textTertiary">vazio</p>
        ) : (
          list.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              view={view}
              status={statuses?.[session.id]}
              folders={folders}
              onOpen={onOpenSession}
            />
          ))
        )}
      </div>
    </section>
  );
}

// Card de sessão (READ-ONLY). Clique abre a sessão. SEM drag.
function SessionCard({ session, view, status, folders, onOpen }) {
  const statusKey = status?.status || 'idle';
  const statusColorVar = STATUS_VAR[statusKey] || STATUS_VAR.idle;

  // Badge do PROJETO real (derivado do diretório); fallback pra pasta manual;
  // senão "Sem projeto". Liga o card ao projeto, não à pasta.
  const folder = session?.folder_id ? (folders || []).find((f) => f.id === session.folder_id) : null;
  const proj = projectFromDirectory(session?.directory);
  const projLabel = proj?.label || folder?.name || 'Sem projeto';
  const projColor = proj?.colorVar || (folder ? folderColorVar(folder.color) : 'var(--apolo-textTertiary)');

  // Borda esquerda: cor do status (visão status) ou cor do projeto (visão projeto).
  const leftColor = view === 'status' ? statusColorVar : projColor;

  const time = relativeTime(session?.last_message_at || session?.updated_at);

  // Linha de output ao vivo = reason do status derivado.
  const reason = status?.reason;
  const reasonClass =
    statusKey === 'blocked'
      ? 'text-apolo-error'
      : statusKey === 'needs_you'
        ? 'text-apolo-warning'
        : 'text-apolo-textTertiary';

  // PR derivado do status (number/url/ci). Só renderiza se existir.
  const pr = status?.pr;
  const ciPass = pr?.ci === 'pass' || pr?.ci === 'success' || pr?.ci === 'passing';
  const ciFail = pr?.ci === 'fail' || pr?.ci === 'failure' || pr?.ci === 'failing';

  return (
    <button
      type="button"
      onClick={() => onOpen(session.id)}
      title={`Abrir sessão: ${session?.name || ''}`}
      style={{ borderLeftColor: leftColor }}
      className="group/cc-card w-full cursor-pointer rounded-apolo-lg border border-l-[3px] border-apolo-border bg-apolo-surface2 p-2.5 text-left transition hover:-translate-y-px hover:border-apolo-borderStrong hover:bg-apolo-surface3 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
    >
      {/* topo: badge pasta + tempo relativo */}
      <div className="mb-1.5 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium text-apolo-textSecondary">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: projColor }} />
          <span className="max-w-[120px] truncate">{projLabel}</span>
        </span>
        {time && <span className="ml-auto font-mono text-[10px] text-apolo-textTertiary">{time}</span>}
      </div>

      {/* título */}
      <div className="truncate text-[13px] font-medium leading-snug text-apolo-textPrimary">
        {session?.name || '(sem nome)'}
      </div>

      {/* output ao vivo (reason) */}
      {reason && (
        <div className={'mt-1.5 flex items-center gap-1.5 truncate font-mono text-[10.5px] ' + reasonClass}>
          {statusKey === 'running' && (
            <span
              aria-hidden="true"
              className="block h-1.5 w-1.5 shrink-0 animate-spin rounded-full border border-current border-t-transparent"
            />
          )}
          <span className="truncate">{reason}</span>
        </div>
      )}

      {/* meta pills: modelo · PR · status · branch */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {session?.model && <Pill>{session.model}</Pill>}
        {pr?.number != null && (
          <Pill
            className={
              ciPass
                ? 'border-apolo-success/40 text-apolo-success'
                : ciFail
                  ? 'border-apolo-error/40 text-apolo-error'
                  : ''
            }
          >
            PR #{pr.number}
            {ciPass ? ' ✓ CI' : ciFail ? ' ✗ CI' : ''}
          </Pill>
        )}
        {/* Pill de status — REUSA SessionStatusBadge (ícone + texto + role=status,
            reason no aria/title). Envolto na moldura de chip do card. */}
        <span className="inline-flex items-center rounded-apolo-sm border border-apolo-border bg-apolo-surface1 px-1.5 py-0.5">
          <SessionStatusBadge status={status || statusKey} />
        </span>
        {session?.branch && <Pill className="text-apolo-textTertiary">⑂ {session.branch}</Pill>}
      </div>
    </button>
  );
}

function Pill({ children, className = '' }) {
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 rounded-apolo-sm border border-apolo-border bg-apolo-surface1 px-1.5 py-0.5 font-mono text-[10px] text-apolo-textSecondary ' +
        className
      }
    >
      {children}
    </span>
  );
}
